import React from 'react';
import {makeStyles} from "@mui/styles";

// MUI Imports
import {
    Chip,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

// My Imports
import HappyBox from "../layout/HappyBox";

import {colorStyles} from "../../constants/colorStyles";

const useStyles = makeStyles({
    root: {
        padding: '20px', marginTop: '20px', marginBottom: '50px'
    }, title: {
        marginBottom: '20px'
    }, basicsContainer: {
        marginBottom: '20px', '& p': {
            whiteSpace: 'pre-wrap', marginBottom: '20px'
        }
    }, ...colorStyles
});

const MotivationResults = (props) => {
    const classes = useStyles();
    const {results} = props;
    let existTie = false;
    let previousScore = null;

    const STAGES = {
        NF: 'Empower Motivation (NF)',
        NT: 'Impact Motivation (NT)',
        SF: 'Practical Motivation (SF)',
        ST: 'Precision / Analytical Motivation (ST)'
    };

    return (<>
        <HappyBox>
            <Typography variant='h4' gutterBottom>
                Motivation Test
            </Typography>

            <Typography>
                <b>What is it?</b>{" "}
            </Typography>
            <Typography>
                It identifies your core motivation category, allowing us to select careers that not only match
                your interests but also align with what energizes and sustains you.
            </Typography>
            <br></br>
            <Typography>
                <b>Why is it used?</b>{" "}
            </Typography>
            <Typography>
                Sometimes, a job may fit your interests but not your deeper motivation, so clarifying this
                distinction helps us make the most accurate and fulfilling career recommendations.
            </Typography>
        </HappyBox>

        <HappyBox>
            <Typography variant='h4' gutterBottom>
                Your Results
            </Typography>
            <div>
                <Typography variant='h5'>
                    Type: <b>{results.stage}</b>
                </Typography>
            </div>
            <br/>

            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Type</TableCell>
                            <TableCell>Score</TableCell>
                            <TableCell>Score</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {results.scores.map(score => {
                            if (!existTie && previousScore === score.score) existTie = true;
                            previousScore = score.score;
                            return (<TableRow key={score.stage}>
                                <TableCell>
                                    <Chip label={score.stage}
                                          className={classes[score.stage.toLowerCase()]}/>
                                </TableCell>
                                <TableCell>
                                    <LinearProgress variant="determinate" value={(score.score * 100 / 40)}
                                                    className={classes.bar}/>
                                </TableCell>
                                <TableCell>{score.score}</TableCell>
                            </TableRow>)
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <br/>
            {existTie && <Typography>
                In the case of a tie between two scores, we interpret the latter score as the direction you are
                moving toward.
                <br/>
                In this case, your latter score is: <b>{results.stage}</b>
                <br/><br/>
            </Typography>}
            {results.stage === STAGES.NF && <div>
                <Typography>
                    Your primary work motivation comes from empowering others: helping others grow, heal, or
                    succeed. You’re driven by purpose and human connection. You want your work to make a tangible,
                    positive difference in people’s lives.
                </Typography>
                <br/>
                <Typography>
                    You likely feel most fulfilled in roles where you can guide, teach, mentor, or support others on
                    a small group or 1:1 scale. Work that feels too large-scale, abstract, or disconnected from
                    people will lack a clear sense of meaning to you and will quickly feel empty or draining. At
                    your core, you’re motivated by purpose, empathy, and contribution: you want to see others thrive
                    and live their best lives--that's what makes work meaningful to you. You need work that allows
                    you to connect deeply, uplift others, and foster growth--not just complete tasks or hit metrics.
                </Typography>
                <br/>
                <Typography>
                    Careers that often align with this motivation include: <b>education, counseling, coaching,
                    psychology, healthcare, human resources, social services, or leadership roles focused on
                    mentorship and development.</b>
                </Typography>
                <br/>
                <Typography>
                    Careers that would NOT be a good fit are anything that's:
                </Typography>
                <ul>
                    <li><b>Highly technical or data-driven</b>:
                        Jobs focused on systems, numbers, or analysis with little interpersonal interaction.
                    </li>
                    <li><b>Task-based with minimal human contact</b>:
                        Work that keeps you behind a screen or focused on processes rather than connecting with
                        people.
                    </li>
                    <li><b>Competitive or cutthroat</b>:
                        Environments where collaboration, empathy, and emotional awareness are undervalued.
                    </li>
                    <li><b>Authoritarian or hierarchical</b>:
                        Workplaces that discourage open communication or mentoring relationships.
                    </li>
                    <li><b>Product- or profit-focused</b>:
                        Roles centered on output or sales without considering human development or well-being.
                    </li>
                    <li><b>Emotionally detached</b>:
                        Any company culture that views emotions or relational needs as unprofessional or irrelevant.
                    </li>
                </ul>
            </div>}

            {results.stage === STAGES.NT && <div>
                <Typography>
                    Your primary work motivation stems from creating large-scale, lasting impact. You’re driven by
                    the desire to make a meaningful difference beyond the individual level. Your primary goal is to
                    improve systems, communities, or even the world at large. You thrive when your work contributes
                    to innovation, progress, or social change, and you’re likely inspired by causes such as
                    environmental sustainability, health equity, or justice. Routine or small-scale work may feel
                    limiting to you; you need to see the bigger picture and know that your efforts matter on a
                    broader scale. At your core, you’re motivated by vision, legacy, and impact: you want your work
                    to create positive change that reaches far and lasts long after you’re gone. You thrive where
                    your work can ripple outward, influencing systems, communities, or the world.
                </Typography>
                <br/>
                <Typography>
                    Careers that often align with this motivation include: <b>public policy, research, social
                    entrepreneurship, think tanks, sustainability, technology innovation, nonprofit leadership, and
                    organizational strategy.</b>
                </Typography>
                <br/>
                <Typography>
                    Careers that would NOT be a good fit are anything that's:
                </Typography>
                <ul>
                    <li><b>Narrow in scope</b>:
                        Jobs that focus on repetitive or routine tasks without a clear connection to a broader
                        mission or social purpose.
                    </li>
                    <li><b>Highly transactional</b>:
                        Work centered on short-term goals (like sales quotas) rather than long-term change.
                    </li>
                    <li><b>Profit-driven without purpose</b>:
                        Companies or industries that prioritize financial gain over social, environmental, or human
                        impact.
                    </li>
                    <li><b>Change-resistant</b>:
                        Traditional, slow-moving organizations or industries where innovation and social
                        responsibility are low priorities.
                    </li>
                    <li><b>Individualistic rather than collective</b>:
                        Environments that reward personal success over collective progress or shared outcomes.
                    </li>
                </ul>
            </div>}

            {results.stage === STAGES.SF && <div>
                <Typography>
                    Your core motivation comes from being useful, taking action, and seeing tangible results. You’re
                    driven by a desire to help by making a direct, visible impact, whether that means fixing a
                    problem, improving a process, or helping someone in a concrete way. You thrive when you can turn
                    ideas into action and see the outcomes of your efforts right away. Abstract discussions or
                    long-term projects without immediate results may feel frustrating--you prefer work where
                    progress is measurable and the effects are clear. This motivation is common among those who
                    enjoy hands-on, service-oriented, or implementation-focused roles.
                </Typography>
                <br/>
                <Typography>
                    At your core, you’re motivated by usefulness, practicality, and visible progress: you find
                    fulfillment in knowing that what you do each day directly improves the world around you. You are
                    most satisfied when you can see, touch, or directly measure the outcome of your work--when
                    effort turns into something concrete, useful, and functional.
                </Typography>
                <br/>
                <Typography>
                    Careers that often align with this motivation include: <b>operations, project management,
                    customer success, trades, logistics, or administrative work.</b>
                </Typography>
                <br/>
                <Typography>
                    Careers that would NOT be a good fit are anything that's:
                </Typography>
                <ul>
                    <li><b>Conceptual or research-heavy</b>:
                        Roles focused on theory, data analysis, or long-term strategy without clear daily results
                        (e.g., academic research, policy analysis).
                    </li>
                    <li><b>Centered on emotional or relational outcomes</b>:
                        Work where success depends primarily on managing others’ emotions or providing empathy
                        (e.g., counseling, HR, therapy).
                    </li>
                    <li><b>Highly bureaucratic</b>:
                        Jobs with excessive red tape, long approval chains, or delayed implementation may feel slow
                        and ineffective.
                    </li>
                    <li><b>Creative without structure</b>:
                        Roles that prioritize innovation, brainstorming, or aesthetic vision over action and
                        execution (e.g., branding, design, marketing strategy).
                    </li>
                    <li><b>Removed from visible results</b>:
                        Environments where impact is indirect or hard to measure (e.g., consulting with long project
                        timelines, administrative oversight roles).
                    </li>
                </ul>
            </div>}

            {results.stage === STAGES.ST && <div>
                <Typography>
                    Your motivation comes from accuracy, structure, and logic. You feel most satisfied when things
                    are organized, efficient, and correct. You take pride in doing things the right way--the first
                    time--and find fulfillment in maintaining high standards and improving systems. You naturally
                    notice details others miss and get satisfaction from bringing order to complexity. Whether it’s
                    numbers, data, or processes, you enjoy analyzing information and finding the most efficient or
                    logical path forward. At your core, you’re motivated by excellence, clarity, and accuracy: you
                    find purpose in keeping things running smoothly and ensuring the integrity of the work itself.
                    You thrive in environments where facts, logic, and precision are valued, and where your
                    attention to detail directly contributes to quality and success.
                </Typography>
                <br/>
                <Typography>
                    Careers that often align with this motivation are: <b>finance, research, data analysis, quality
                    control, engineering, and law—roles where precision, consistency, and critical thinking are key
                    to success.</b>
                </Typography>
                <br/>
                <Typography>
                    Careers that would NOT be a good fit are anything that's:
                </Typography>
                <ul>
                    <li><b>Emotionally charged or unpredictable:</b>
                        Jobs requiring high emotional labor, such as
                        counseling, crisis response, or social work, can feel overwhelming or inefficient.
                    </li>
                    <li><b>Highly ambiguous or unstructured</b>:
                        Creative fields with no clear standards (e.g.,
                        design, early-stage startups) may feel chaotic or lacking direction.
                    </li>
                    <li><b>Driven by relationships or persuasion</b>:
                        Sales, fundraising, or people-heavy leadership
                        roles that depend on charm, influence, or constant interpersonal interaction may not align
                        with your natural strengths.
                    </li>
                    <li><b>Process-light environments</b>:
                        Workplaces that move fast without documentation,
                        protocols, or systems can feel disorganized and stressful.
                    </li>
                    <li><b>Subjective success measures</b>:
                        Roles where success is hard to quantify (e.g., artistic,
                        visionary, or counseling roles) may feel uncomfortable or unrewarding.
                    </li>

                    <li><b>Narrow in scope</b>:
                        Jobs that focus on repetitive or routine tasks without a clear connection to a broader
                        mission or social purpose.
                    </li>
                    <li><b>Highly transactional</b>:
                        Work centered on short-term goals (like sales quotas) rather than long-term change.
                    </li>
                    <li><b>Profit-driven without purpose</b>:
                        Companies or industries that prioritize financial gain over social, environmental, or human
                        impact.
                    </li>
                    <li><b>Change-resistant</b>:
                        Traditional, slow-moving organizations or industries where innovation and social
                        responsibility are low priorities.
                    </li>
                    <li><b>Individualistic rather than collective</b>:
                        Environments that reward personal success over collective progress or shared outcomes.
                    </li>
                </ul>
            </div>}
        </HappyBox>
    </>)
}

export default MotivationResults
